import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { PortalRegion, detectPortalRegion } from './portal-region';

const LEGACY_PORTAL_MODULE = 'teccom';

/** Legacy applications a deeplink may lead to, as subdomains of the region's legacy domain. */
const TRUSTED_LEGACY_MODULES: readonly string[] = [LEGACY_PORTAL_MODULE, 'returns', 'connectweb', 'validationcertification', 'dashboard'];

/**
 * Single source of truth for portal URLs: the region of the user and where the Shell
 * and the Legacy applications live for that region.
 */
@Injectable({ providedIn: 'root' })
export class PortalUrls {
  readonly region: PortalRegion = detectPortalRegion();
  readonly shellOrigin: string = window.location.origin;

  /** Absolute URL of a Legacy portal (TecWeb) page, e.g. `legacyPortal('/home/dashboard')`. */
  legacyPortal(path: string): string {
    return this.legacyModule(LEGACY_PORTAL_MODULE, `/newapp${path}`);
  }

  /** Absolute URL of a page of any Legacy application, e.g. `legacyModule('returns', '/newapp/returns/claimlist')`. */
  legacyModule(module: string, path: string): string {
    return `${this.localLegacyOrigin ?? `https://${module}${this.legacyDomainSuffix}`}${path}`;
  }

  /** Whether an absolute URL is one of the given Legacy portal (TecWeb) pages; query, fragment and trailing slash do not matter. */
  isLegacyPortalPage(url: URL, paths: readonly string[]): boolean {
    const pathname = url.pathname.replace(/\/+$/, '');
    return paths.some(path => {
      const page = new URL(this.legacyPortal(path));
      return url.origin === page.origin && pathname === page.pathname;
    });
  }

  /** Whether an absolute URL belongs to the portal: the Shell itself or a trusted Legacy application. */
  isPortalUrl(url: URL): boolean {
    return url.origin === this.shellOrigin
      || url.origin === this.localLegacyOrigin
      || (url.protocol === 'https:' && TRUSTED_LEGACY_MODULES.some(module => url.hostname === `${module}${this.legacyDomainSuffix}`));
  }

  private get legacyDomainSuffix(): string {
    return this.region === PortalRegion.China ? environment.chineseLegacyDomainSuffix : environment.legacyDomainSuffix;
  }

  /** Local development only: Legacy runs on a fixed origin, there are no module subdomains on localhost. */
  private get localLegacyOrigin(): string | undefined {
    return (environment as { legacyOrigin?: string }).legacyOrigin;
  }
}
